import { CombinedGraphQLErrors, ServerError } from '@apollo/client';
import type { ErrorLink } from '@apollo/client/link/error';
import { errorType } from '@guuru/graphql-common';
import { Sentry } from '@guuru/sentry-web';

const refreshInterbalMs = process.env.REFRESH_INTERVAL_MS || 5 * 60 * 1000;
const userErrorCodes = Object.keys(errorType);

const reloadOnSchemaError = (code: string) => {
  if (code !== 'GRAPHQL_VALIDATION_FAILED') return false;

  const lastCheckAt = Number(window.localStorage.getItem('lastVersionCheckAt'));
  if (lastCheckAt > Date.now() - Number(refreshInterbalMs)) return false;

  window.localStorage.setItem('lastVersionCheckAt', String(Date.now()));
  window.location.reload();
  return true;
};

const errorCallback: ErrorLink.ErrorHandler = ({ operation, error }) => {
  const { operationName, variables } = operation;
  // The browser GraphQL integration collects documents, but not variables.
  const extra = Sentry.getClient()?.getDataCollectionOptions().graphQL.variables
    ? { variables }
    : {};

  if (CombinedGraphQLErrors.is(error)) {
    error.errors.forEach(({ message, path, extensions = {} }) => {
      const code = typeof extensions.code === 'string' ? extensions.code : '';
      const { exception } = extensions;

      if (userErrorCodes.includes(code) || reloadOnSchemaError(code)) return;

      Sentry.captureMessage(message, {
        tags: {
          operation: operationName,
          code,
        },
        extra: {
          ...(typeof exception === 'object' && exception !== null
            ? exception
            : {}),
          ...extra,
          path,
        },
        level: 'error',
      });
      console.error(`[GraphQL error]: ${message}`, operationName);
    });
  }

  if (ServerError.is(error)) {
    Sentry.captureException(error, {
      tags: {
        operation: operationName,
      },
      extra: {
        ...extra,
        status: error.statusCode,
        statusText: error.response.statusText,
      },
    });
    console.error(error);
  }
};

export default errorCallback;
